import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

// List of option paths
const OPTION_PATHS = {
  suppliers: "suppliers",
  consignees: "consignees",
  emptyLocations: "unload-venues",
  status: "status",
  orderStatuses: "order-statuses",
  type: "container-types",
  shipping: "shipping-documents",
  vessal: "vessels",
  logistics: "logistics-providers",
  material: "materials"
};

const OptionsContext = createContext();

export const OptionsProvider = ({ children }) => {
  const { token, selectedOrgId } = useAuth();
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  // Helper to fetch one path
  const fetchPath = useCallback(async (pathKey) => {
    const path = OPTION_PATHS[pathKey];
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_NETWORK}/${path}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            ...(selectedOrgId ? { "X-Active-Org": selectedOrgId.toString() } : {}),
            "skip_zrok_interstitial": "true",
          },
          
        }
      );

      let data = res.data;
      if (typeof data === "string") data = JSON.parse(data);

      return data.data.map((row) => {
        if (pathKey === "orderStatuses") {
          return {
            id: row[0],
            key: row[2] || row[1],
            label: row[1],
            name: row[1],
            code: row[2] || row[1],
            sequence_order: row[3] || 10,
            progress: row[4] !== undefined ? row[4] : 50,
            color: row[5] || "bg-blue-500",
            badge_color: row[6] || "bg-blue-50 text-blue-800 border-blue-200",
            badgeColor: row[6] || "bg-blue-50 text-blue-800 border-blue-200",
          };
        }
        if (pathKey === "consignees") {
          return {
            id: row[0],
            name: row[1],
            org_id: row[2] !== undefined ? row[2] : null,
            code: row[3] || null,
          };
        }
        if (pathKey === "suppliers") {
          return {
            id: row[0],
            name: row[1],
            variance_threshold_pct: row[2] !== undefined && row[2] !== null ? Number(row[2]) : 2.0,
            default_currency: row[3] || "USD",
            payment_terms: row[4] || "",
            payment_term: row[4] ? { name: row[4] } : null
          };
        }
        if (row.length >= 3) {
            return { id: row[0], name: row[1], freeDays: row[2] };
        }
        return { id: row[0], name: row[1] };
      });
    } catch (err) {
      console.error(`Failed to fetch ${pathKey}:`, err);
      throw err;
    }
  }, [selectedOrgId]);

  // Refresh tenant-scoped options when the active organisation changes.
  const fetchAllOptions = async () => {
    setLoading(true);
    const newOptions = {};
    const newErrors = {};

    await Promise.all(
      Object.keys(OPTION_PATHS).map(async (key) => {
        try {
          newOptions[key] = await fetchPath(key);
        } catch (error) {
          newOptions[key] = [];
          newErrors[key] = true;
        }
      })
    );

    setOptions(newOptions);
    setErrors(newErrors);
    setLoading(false);
  };

  // Refresh specific path
  const refresh = useCallback(async (key) => {
    try {
      const updated = await fetchPath(key);
      setOptions((prev) => ({ ...prev, [key]: updated }));
      setErrors((prev) => ({ ...prev, [key]: false }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [key]: true }));
    }
  }, [fetchPath]);

  useEffect(() => {
    if (token) fetchAllOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, fetchPath]);

  const value = useMemo(
    () => ({ options, loading, errors, refresh }),
    [options, loading, errors, refresh]
  );

  return (
    <OptionsContext.Provider value={value}>
      {children}
    </OptionsContext.Provider>
  );
};

export const useOptionsContext = () => {
  const ctx = useContext(OptionsContext);
  if (!ctx) throw new Error("useOptionsContext must be used within OptionsProvider");
  return ctx;
};
